(() => {
  const config = window.themeSmoothScroll || {};
  const root = document.documentElement;

  const enabled = Boolean(config.enabled);
  const respectReducedMotion = config.respectReducedMotion !== false;
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (!enabled || (respectReducedMotion && prefersReducedMotion)) {
    return;
  }

  const duration = Math.max(100, Number(config.duration) || 700);
  const offset = Math.max(0, Number(config.offset) || 0);
  const easingName = config.easing || 'ease_out';

  const easingMap = {
    linear: (t) => t,
    ease_in_out: (t) => {
      if (t < 0.5) return 2 * t * t;
      return 1 - Math.pow(-2 * t + 2, 2) / 2;
    },
    ease_out: (t) => 1 - Math.pow(1 - t, 3),
  };

  const gsapEaseMap = {
    linear: 'none',
    ease_in_out: 'power2.inOut',
    ease_out: 'power3.out',
  };

  const ease = easingMap[easingName] || easingMap.ease_out;
  const gsapEase = gsapEaseMap[easingName] || gsapEaseMap.ease_out;
  const isTouchLikeViewport = window.matchMedia('(hover: none), (pointer: coarse)').matches;
  const isProductPage = document.body && document.body.classList.contains('template-product');

  let animationFrameId = null;
  let smoother = null;
  let refreshTimeoutId = null;

  const cancelAnimation = () => {
    if (animationFrameId) {
      cancelAnimationFrame(animationFrameId);
      animationFrameId = null;
    }
  };

  const resetNativeScrollShell = () => {
    const wrapper = document.getElementById('smooth-wrapper');
    const content = document.getElementById('smooth-content');

    root.classList.remove('smooth-scroll-active');
    root.classList.remove('smooth-scroll-enabled');

    [document.documentElement, document.body, wrapper, content].forEach((node) => {
      if (!node || !node.style) return;

      node.style.removeProperty('overflow');
      node.style.removeProperty('overflow-x');
      node.style.removeProperty('overflow-y');
      node.style.removeProperty('position');
      node.style.removeProperty('height');
      node.style.removeProperty('max-height');
      node.style.removeProperty('min-height');
      node.style.removeProperty('top');
      node.style.removeProperty('left');
      node.style.removeProperty('right');
      node.style.removeProperty('bottom');
      node.style.removeProperty('width');
      node.style.removeProperty('transform');
      node.style.removeProperty('will-change');
    });
  };

  const findTarget = (hash) => {
    if (!hash) return null;

    const id = decodeURIComponent(hash.replace(/^#/, ''));
    if (!id) return document.body;

    const byId = document.getElementById(id);
    if (byId) return byId;

    if (window.CSS && typeof window.CSS.escape === 'function') {
      return document.querySelector(`[name="${window.CSS.escape(id)}"]`);
    }

    return document.querySelector(`[name="${id.replace(/"/g, '\\"')}"]`);
  };

  const targetYForElement = (element) => {
    if (!element || element === document.body) return 0;

    const rawY = element.getBoundingClientRect().top + window.scrollY - offset;
    return Math.max(0, rawY);
  };

  const animateScrollTo = (targetY) => {
    if (isTouchLikeViewport) {
      window.scrollTo(0, targetY);
      return;
    }

    cancelAnimation();

    const startY = window.scrollY;
    const delta = targetY - startY;

    if (Math.abs(delta) < 1) {
      window.scrollTo(0, targetY);
      return;
    }

    const startTime = performance.now();

    const step = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = ease(progress);

      window.scrollTo(0, startY + delta * eased);

      if (progress < 1) {
        animationFrameId = requestAnimationFrame(step);
      } else {
        animationFrameId = null;
      }
    };

    animationFrameId = requestAnimationFrame(step);
  };

  const scrollToTarget = (target, smooth = true) => {
    if (!target) return;

    if (smoother && typeof smoother.scrollTo === 'function') {
      const position = offset > 0 ? `top ${offset}px` : undefined;
      smoother.scrollTo(target, smooth, position);
      return;
    }

    if (isTouchLikeViewport) {
      window.scrollTo(0, targetYForElement(target));
      return;
    }

    animateScrollTo(targetYForElement(target));
  };

  const refreshSmoother = () => {
    if (!window.ScrollTrigger) return;

    if (smoother && typeof smoother.refresh === 'function') {
      smoother.refresh();
      return;
    }

    window.ScrollTrigger.refresh();
  };

  const scheduleRefresh = (delay = 0) => {
    if (!window.ScrollTrigger) return;

    if (refreshTimeoutId) {
      window.clearTimeout(refreshTimeoutId);
    }

    refreshTimeoutId = window.setTimeout(() => {
      refreshTimeoutId = null;
      window.requestAnimationFrame(() => {
        refreshSmoother();
      });
    }, delay);
  };

  const setupAnchorHandling = () => {
    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[href]');
      if (!link) return;

      const href = link.getAttribute('href');
      if (!href || href === '#') return;

      let url;
      try {
        url = new URL(link.href, window.location.href);
      } catch {
        return;
      }

      if (url.origin !== window.location.origin || url.pathname !== window.location.pathname || !url.hash) {
        return;
      }

      const target = findTarget(url.hash);
      if (!target) return;

      event.preventDefault();

      if (window.location.hash !== url.hash) {
        history.pushState(null, '', url.hash);
      }

      scrollToTarget(target, true);
    });

    window.addEventListener('popstate', () => {
      if (!window.location.hash) return;

      const target = findTarget(window.location.hash);
      if (!target) return;

      scrollToTarget(target, true);
    });

    if (window.location.hash) {
      window.requestAnimationFrame(() => {
        const target = findTarget(window.location.hash);
        if (!target) return;

        scrollToTarget(target, false);
      });
    }
  };

  const initializeGsapSmoother = () => {
    if (!window.gsap || !window.ScrollTrigger) {
      root.classList.remove('smooth-scroll-active');
      return;
    }

    if (typeof window.gsap.registerPlugin === 'function') {
      window.gsap.registerPlugin(window.ScrollTrigger);
      if (window.ScrollSmoother) {
        window.gsap.registerPlugin(window.ScrollSmoother);
      }
    }

    if (typeof window.ScrollTrigger.config === 'function') {
      window.ScrollTrigger.config({ ignoreMobileResize: true });
    }

    if (isTouchLikeViewport) {
      resetNativeScrollShell();
      return;
    }

    if (isProductPage) {
      resetNativeScrollShell();
      return;
    }

    if (!window.ScrollSmoother) {
      root.classList.remove('smooth-scroll-active');
      return;
    }

    const wrapper = document.getElementById('smooth-wrapper');
    const content = document.getElementById('smooth-content');

    if (!wrapper || !content) return;
    const existingSmoother = window.ScrollSmoother.get();
    if (existingSmoother) {
      existingSmoother.kill();
    }

    smoother = window.ScrollSmoother.create({
      wrapper,
      content,
      smooth: duration / 1000,
      smoothTouch: 0,
      effects: true,
      ease: gsapEase,
    });

    root.classList.add('smooth-scroll-active');
  };

  const setupRefreshHandling = () => {
    const refreshEvents = [
      'theme:layout:changed',
      'shopify:section:load',
      'shopify:section:unload',
      'shopify:section:reorder',
      'shopify:block:select',
      'shopify:block:deselect',
    ];

    refreshEvents.forEach((eventName) => {
      document.addEventListener(eventName, () => {
        scheduleRefresh(80);
      });
    });

    window.addEventListener('load', () => {
      scheduleRefresh(0);
      scheduleRefresh(180);
    });

    window.addEventListener('pageshow', () => {
      scheduleRefresh(0);
    });

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready
        .then(() => {
          scheduleRefresh(0);
          scheduleRefresh(120);
        })
        .catch(() => {});
    }
  };

  initializeGsapSmoother();
  setupRefreshHandling();
  setupAnchorHandling();
})();
